import { useCallback, useEffect, useRef } from 'react';
import {
  anchorAtPoint,
  paintSelection,
  pointAfter,
  rangeBetween,
  ReaderSelection,
  readerSelectionFromOffsets,
  wordRangeAtPoint,
  WordRange,
} from '../lib/reader/selection';

export interface MultiSelection {
  text: string;
  rect: DOMRect;
}

// A plain tap that did not become a selection, with the reader block under it
// (null when the tap landed on a heading/gap), the char offset of the tapped
// word within that block (for word-level actions like audio seek), and its
// viewport coordinates. The book reader uses `blockId` to open that scene's
// tools; the translation reader uses both to seek narration to the tapped word.
export interface ReaderTap {
  blockId: string | null;
  charOffset: number | null;
  x: number;
  y: number;
}

interface Options {
  // Selection is wired up only while enabled (e.g. not while a sheet is open).
  enabled: boolean;
  // A finalised selection inside one paragraph, ready for the WordingSheet.
  onSelect: (sel: ReaderSelection) => void;
  // A finalised selection spanning more than one paragraph (so it can't be
  // reworded) — offered for copy/share instead.
  onSelectMulti?: (sel: MultiSelection) => void;
  // A plain tap/click on the reading surface that did not become a selection.
  onTap?: (tap: ReaderTap) => void;
}

// Hold this long on a word (without scrolling away) to start a selection.
const LONG_PRESS_MS = 400;
// Finger travel that still counts as a "still" press rather than a scroll.
const MOVE_SLOP = 10;
// While selecting, dragging within this many px of the top/bottom edge scrolls
// the text so the selection can run onto the next/previous line — and past the
// visible page. Speed scales with how deep into the zone the pointer is.
const EDGE_ZONE = 72;
const MAX_EDGE_SPEED = 14;

/**
 * Reader text selection on top of point hit-testing, so the browser's native
 * selection can stay suppressed (user-select: none) — which stops mobile
 * Chrome/Safari popping their selection toolbar over our rewording sheet.
 *
 * Touch: long-press a word to start selecting, then drag to extend — across
 * words and across paragraphs. Dragging to the top/bottom edge scrolls the text
 * a line at a time, so a selection can run past the visible page. A plain tap is
 * not a selection (it fires onTap). Mouse: press-and-drag to select. A selection
 * within one paragraph opens the rewording sheet; one spanning several is
 * offered for copy/share.
 */
export function useReaderSelection({ enabled, onSelect, onSelectMulti, onTap }: Options) {
  const wasSelectingRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  const onSelectMultiRef = useRef(onSelectMulti);
  const onTapRef = useRef(onTap);
  useEffect(() => {
    onSelectRef.current = onSelect;
    onSelectMultiRef.current = onSelectMulti;
    onTapRef.current = onTap;
  }, [onSelect, onSelectMulti, onTap]);

  useEffect(() => {
    if (!enabled) return undefined;

    // The long-pressed word, and the moving focus point (which may be in another
    // paragraph). The selection spans the anchor word's far edge to the focus.
    let anchor: WordRange | null = null;
    let focus: { para: HTMLElement; off: number } | null = null;
    let startPoint: { x: number; y: number } | null = null;
    let mode: 'idle' | 'pending' | 'selecting' = 'idle';
    let timer: number | null = null;
    let lastPoint: { x: number; y: number } | null = null;
    let edgeRaf: number | null = null;
    // After a touch, the browser synthesizes a mouse{down,up} pair for the same
    // tap. Without this guard onTap fires twice (touch + synthetic mouse) and the
    // two toggles cancel out, so the immersive chrome never appears on a phone.
    let lastTouchAt = 0;
    const SYNTHETIC_MOUSE_MS = 700;

    const clearTimer = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const surface = () => document.querySelector<HTMLElement>('[data-reader-surface]');

    const repaint = () => {
      if (!anchor || !focus) return;
      const aOff = pointAfter(focus.para, focus.off, anchor.para, anchor.start)
        ? anchor.start
        : anchor.end;
      paintSelection(rangeBetween(anchor.para, aOff, focus.para, focus.off));
    };

    const extendTo = (x: number, y: number) => {
      if (!anchor) return;
      const w = wordRangeAtPoint(x, y);
      if (!w) return;
      const forward = pointAfter(w.para, w.end, anchor.para, anchor.start);
      focus = { para: w.para, off: forward ? w.end : w.start };
      repaint();
    };

    const stopEdge = () => {
      if (edgeRaf !== null) cancelAnimationFrame(edgeRaf);
      edgeRaf = null;
    };

    const edgeSpeed = (depth: number) =>
      Math.ceil((Math.min(Math.max(depth, 0), EDGE_ZONE) / EDGE_ZONE) * MAX_EDGE_SPEED);

    // While the pointer sits near the top/bottom edge during a selection, scroll
    // the reader and re-extend the selection to the line revealed under it.
    const edgeTick = () => {
      edgeRaf = null;
      if (mode !== 'selecting' || !lastPoint) return;
      const el = surface();
      if (!el) return;
      const rect = el.getBoundingClientRect();
      let v = 0;
      if (lastPoint.y < rect.top + EDGE_ZONE) v = -edgeSpeed(rect.top + EDGE_ZONE - lastPoint.y);
      else if (lastPoint.y > rect.bottom - EDGE_ZONE)
        v = edgeSpeed(lastPoint.y - (rect.bottom - EDGE_ZONE));
      if (v === 0) return; // pointer left the edge zone → stop the loop
      el.scrollTop += v;
      extendTo(lastPoint.x, lastPoint.y);
      edgeRaf = requestAnimationFrame(edgeTick);
    };

    const track = (x: number, y: number) => {
      lastPoint = { x, y };
      extendTo(x, y);
      if (mode === 'selecting' && edgeRaf === null) edgeRaf = requestAnimationFrame(edgeTick);
    };

    const beginSelecting = () => {
      if (!anchor) return;
      mode = 'selecting';
      wasSelectingRef.current = true;
      focus = { para: anchor.para, off: anchor.end };
      repaint();
    };

    const finish = () => {
      if (!anchor || !focus) return;
      if (anchor.para === focus.para) {
        const start = Math.min(anchor.start, focus.off);
        const end = Math.max(anchor.end, focus.off);
        const sel = readerSelectionFromOffsets(anchor.para, start, end);
        if (sel) onSelectRef.current(sel);
        return;
      }
      const aOff = pointAfter(focus.para, focus.off, anchor.para, anchor.start)
        ? anchor.start
        : anchor.end;
      const r = rangeBetween(anchor.para, aOff, focus.para, focus.off);
      const text = r.toString();
      if (text.trim()) onSelectMultiRef.current?.({ text, rect: r.getBoundingClientRect() });
    };

    const reset = () => {
      clearTimer();
      stopEdge();
      anchor = null;
      focus = null;
      startPoint = null;
      lastPoint = null;
      mode = 'idle';
    };

    // Whether a point lies on the reading surface (vs. the header/footer chrome),
    // so taps on buttons never toggle chrome or start a selection.
    const onSurface = (target: EventTarget | null) =>
      !!(target instanceof Element && target.closest('[data-reader-surface]'));
    const isTap = (x: number, y: number) =>
      !startPoint || Math.hypot(x - startPoint.x, y - startPoint.y) <= MOVE_SLOP;
    // The reader block sitting under a tap (null over a heading/gap) and the char
    // offset of the tapped word within it, so callers can act on the specific
    // paragraph/scene — or the specific word — that was tapped.
    const emitTap = (x: number, y: number) => {
      const anchor = anchorAtPoint(x, y);
      onTapRef.current?.({
        blockId: anchor?.segmentId ?? null,
        charOffset: anchor?.charOffset ?? null,
        x,
        y,
      });
    };

    const onTouchStart = (e: TouchEvent) => {
      reset();
      wasSelectingRef.current = false;
      if (e.touches.length !== 1 || !onSurface(e.target)) return;
      const t = e.touches[0];
      startPoint = { x: t.clientX, y: t.clientY };
      mode = 'pending';
      const w = wordRangeAtPoint(t.clientX, t.clientY);
      if (w) {
        anchor = w;
        timer = window.setTimeout(beginSelecting, LONG_PRESS_MS);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || mode === 'idle') return;
      if (mode === 'pending') {
        const moved =
          startPoint && Math.hypot(t.clientX - startPoint.x, t.clientY - startPoint.y) > MOVE_SLOP;
        if (moved) reset(); // a drag here is a scroll / page swipe, not a select
        return;
      }
      e.preventDefault();
      track(t.clientX, t.clientY);
    };

    const onTouchEnd = (e: TouchEvent) => {
      const t = e.changedTouches[0];
      if (mode === 'selecting') {
        finish();
      } else if (mode === 'pending' && t && isTap(t.clientX, t.clientY)) {
        emitTap(t.clientX, t.clientY);
      }
      lastTouchAt = Date.now();
      reset();
    };

    const onMouseDown = (e: MouseEvent) => {
      if (Date.now() - lastTouchAt < SYNTHETIC_MOUSE_MS) return;
      reset();
      wasSelectingRef.current = false;
      if (e.button !== 0 || !onSurface(e.target)) return;
      startPoint = { x: e.clientX, y: e.clientY };
      mode = 'pending';
      anchor = wordRangeAtPoint(e.clientX, e.clientY);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (mode === 'idle' || e.buttons === 0) return;
      if (mode === 'pending') {
        const moved =
          startPoint && Math.hypot(e.clientX - startPoint.x, e.clientY - startPoint.y) > MOVE_SLOP;
        if (moved && anchor) {
          beginSelecting();
          track(e.clientX, e.clientY);
        }
        return;
      }
      track(e.clientX, e.clientY);
    };

    const onMouseUp = (e: MouseEvent) => {
      if (mode === 'selecting' && anchor && focus) {
        finish();
      } else if (mode === 'pending' && isTap(e.clientX, e.clientY)) {
        emitTap(e.clientX, e.clientY);
      }
      reset();
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      reset();
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [enabled]);

  const clear = useCallback(() => {
    wasSelectingRef.current = false;
    paintSelection(null);
  }, []);
  const isSelecting = useCallback(() => wasSelectingRef.current, []);

  return { clear, isSelecting };
}
